import React from 'react';
import { Card, Button, Spin, Tag, Empty } from 'antd';
import { WalletOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { MerchantDetailData } from '../../hooks/useMerchantDetail';
import { useMerchantAnalytics } from '../../hooks/useMerchantAnalytics';
import { formatVnd } from '../../utils/formatVnd';
import { formatRelativeVn } from '../../utils/vnDateTime';

interface GeneralInfoTabProps {
  merchant: MerchantDetailData | null;
  merchantId?: string;
  onEditClick?: () => void;
  onViewReportClick?: () => void;
}

export const GeneralInfoTab: React.FC<GeneralInfoTabProps> = ({
  merchant,
  merchantId,
  onEditClick,
  onViewReportClick,
}) => {
  const { stats, statsLoading } = useMerchantAnalytics(merchantId, {
    range: 'lifetime',
  });

  if (!merchant) return null;

  const formatStat = (value: number | null | undefined) => {
    if (value == null) return '–';
    return value.toLocaleString('vi-VN');
  };
  const formatRating = (value: number | null | undefined) => {
    if (value == null || value === 0) return '–';
    return value.toFixed(1);
  };

  const hours = merchant.operatingHours;
  const bank = merchant.bankAccount;
  const activities = merchant.recentActivity ?? [];

  return (
    <div className="merchant-detail-general-info">
      <div className="merchant-detail-general-info-grid">
        <div className="merchant-detail-general-info-left">
          <Card
            className="merchant-detail-info-card"
            title="Business Details"
            extra={
              <button
                type="button"
                className="merchant-detail-edit-link"
                onClick={onEditClick}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1890ff',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Edit
              </button>
            }
          >
            <div className="merchant-detail-info-grid-inner">
              <div className="merchant-detail-info-block">
                <div className="merchant-detail-info-label">OWNER NAME</div>
                <div className="merchant-detail-info-value">
                  {merchant.name || '–'}
                </div>
              </div>
              <div className="merchant-detail-info-block">
                <div className="merchant-detail-info-label">PHONE NUMBER</div>
                <div className="merchant-detail-info-value">
                  {merchant.phone || '–'}
                </div>
              </div>
              <div className="merchant-detail-info-block">
                <div className="merchant-detail-info-label">
                  BUSINESS ADDRESS
                </div>
                <div className="merchant-detail-info-value">
                  {merchant.address || merchant.restaurantAddress || '–'}
                </div>
              </div>
              <div className="merchant-detail-info-block">
                <div className="merchant-detail-info-label">EMAIL ADDRESS</div>
                <div className="merchant-detail-info-value">
                  {merchant.email || '–'}
                </div>
              </div>
              <div className="merchant-detail-info-block">
                <div className="merchant-detail-info-label">TAX ID</div>
                <div className="merchant-detail-info-value">
                  {merchant.taxId || 'Chưa cập nhật'}
                </div>
              </div>
            </div>
          </Card>
          <Card
            className="merchant-detail-info-card"
            title="Operating Hours"
            extra={
              hours ? (
                <Tag
                  color={
                    hours.manualOpen && hours.withinHours !== false
                      ? 'green'
                      : 'default'
                  }
                >
                  {hours.manualOpen
                    ? hours.withinHours
                      ? 'Đang mở cửa'
                      : 'Ngoài giờ'
                    : 'Tạm đóng (manual)'}
                </Tag>
              ) : null
            }
          >
            {!hours || hours.isConfigured === false ? (
              <div className="merchant-detail-info-value">Chưa cấu hình</div>
            ) : (
              <div className="merchant-detail-hours">
                <div className="merchant-detail-hours-summary">
                  <ClockCircleOutlined style={{ marginRight: 6 }} />
                  {hours.summary}
                </div>
                <ul className="merchant-detail-hours-list">
                  {hours.days.map(day => (
                    <li
                      key={day.dayOfWeek}
                      className="merchant-detail-hours-row"
                    >
                      <span className="merchant-detail-hours-day">
                        {day.dayLabel}
                      </span>
                      <span
                        className={
                          day.enabled
                            ? 'merchant-detail-hours-time'
                            : 'merchant-detail-hours-closed'
                        }
                      >
                        {day.hours}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        </div>
        <div className="merchant-detail-general-info-right">
          <Card className="merchant-detail-info-card" title="Quick Stats">
            <div className="merchant-detail-quick-stat">
              <span className="merchant-detail-quick-stat-label">
                <WalletOutlined style={{ marginRight: 6 }} />
                Điểm khả dụng (Credit)
              </span>
              <span className="merchant-detail-quick-stat-value">
                {merchant.creditBalance != null
                  ? `${Number(merchant.creditBalance).toLocaleString(
                      'vi-VN'
                    )} điểm`
                  : '–'}
              </span>
            </div>
            <div className="merchant-detail-quick-stat">
              <span className="merchant-detail-quick-stat-label">
                Total Orders (all time)
              </span>
              <span className="merchant-detail-quick-stat-value">
                {statsLoading ? (
                  <Spin size="small" />
                ) : (
                  formatStat(stats?.lifetime_orders ?? stats?.total_orders)
                )}
              </span>
            </div>
            <div className="merchant-detail-quick-stat">
              <span className="merchant-detail-quick-stat-label">
                Lifetime Revenue
              </span>
              <span className="merchant-detail-quick-stat-value">
                {statsLoading ? (
                  <Spin size="small" />
                ) : stats?.lifetime_revenue != null ? (
                  formatVnd(stats.lifetime_revenue)
                ) : (
                  '–'
                )}
              </span>
            </div>
            <div className="merchant-detail-quick-stat">
              <span className="merchant-detail-quick-stat-label">
                Average Rating
              </span>
              <span className="merchant-detail-quick-stat-value">
                {statsLoading ? (
                  <Spin size="small" />
                ) : (
                  formatRating(stats?.avg_rating)
                )}
              </span>
            </div>
            <Button
              type="link"
              className="merchant-detail-view-report-btn"
              onClick={onViewReportClick}
            >
              View Full Report
            </Button>
          </Card>
          <Card className="merchant-detail-info-card" title="Bank Account">
            {!bank?.hasBankDetails ? (
              <div className="merchant-detail-info-value">
                Merchant chưa cập nhật thông tin ngân hàng
              </div>
            ) : (
              <div className="merchant-detail-bank-grid">
                <div className="merchant-detail-info-block">
                  <div className="merchant-detail-info-label">Ngân hàng</div>
                  <div className="merchant-detail-info-value">
                    {bank.bankName}
                    {bank.bankCode ? ` (${bank.bankCode})` : ''}
                  </div>
                </div>
                <div className="merchant-detail-info-block">
                  <div className="merchant-detail-info-label">Số tài khoản</div>
                  <div className="merchant-detail-info-value">
                    {bank.bankAccountNumber}
                  </div>
                </div>
                <div className="merchant-detail-info-block">
                  <div className="merchant-detail-info-label">
                    Chủ tài khoản
                  </div>
                  <div className="merchant-detail-info-value">
                    {bank.bankAccountName || '–'}
                  </div>
                </div>
                {bank.paymentNote ? (
                  <div className="merchant-detail-info-block merchant-detail-info-block-full">
                    <div className="merchant-detail-info-label">
                      Ghi chú chuyển khoản
                    </div>
                    <div className="merchant-detail-info-value">
                      {bank.paymentNote}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </Card>
          <Card className="merchant-detail-info-card" title="Recent Activity">
            {activities.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Chưa có hoạt động gần đây"
              />
            ) : (
              <ul className="merchant-detail-activity-list">
                {activities.map(item => (
                  <li key={item.id} className="merchant-detail-activity-item">
                    <div className="merchant-detail-activity-main">
                      <span className="merchant-detail-activity-title">
                        Đơn {item.orderNumber}
                      </span>
                      <Tag className="merchant-detail-activity-status">
                        {item.statusLabel || item.status}
                      </Tag>
                    </div>
                    <div className="merchant-detail-activity-meta">
                      <span>{item.description}</span>
                      <span className="merchant-detail-activity-time">
                        {formatRelativeVn(item.timestamp)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};
